import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "../components/ui/card";
import { CheckCircle2, Loader2, XCircle, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 3000;
const MAX_POLL = 40; // ~2 min

export default function AdhesionReturnPage() {
  const [params] = useSearchParams();
  const applicationId = params.get("application_id");
  const cancelled = params.get("status") === "cancelled";
  const [state, setState] = useState({ loading: true, status: null, sumupStatus: null });
  const attempts = useRef(0);

  useEffect(() => {
    if (!applicationId) return;
    let timer;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/sumup/status/${applicationId}`);
        setState({ loading: false, status: data.application_status, sumupStatus: data.sumup_status });
        attempts.current += 1;
        if (data.application_status === "pending_payment" && attempts.current < MAX_POLL && !cancelled) {
          timer = setTimeout(poll, POLL_MS);
        }
      } catch (e) {
        setState({ loading: false, status: "error", sumupStatus: null });
      }
    };
    poll();
    return () => timer && clearTimeout(timer);
  }, [applicationId, cancelled]);

  if (!applicationId) {
    return <ReturnLayout tone="error" icon={<XCircle className="h-12 w-12 text-red-500" />}
      title="Référence manquante" message="Aucune demande à afficher." />;
  }

  if (cancelled) {
    return <ReturnLayout tone="warn" icon={<XCircle className="h-12 w-12 text-orange-500" />}
      title="Paiement annulé"
      message="Vous avez interrompu le paiement. Vous pouvez recommencer votre demande."
      cta={{ href: "/adhesion", label: "Retourner au formulaire" }} />;
  }

  if (state.loading) {
    return <ReturnLayout tone="info" icon={<Loader2 className="h-12 w-12 text-accent animate-spin" />}
      title="Chargement…" message="Vérification du statut de votre paiement." />;
  }

  if (state.status === "paid_pending_review") {
    return <ReturnLayout tone="success" icon={<CheckCircle2 className="h-12 w-12 text-emerald-500" />}
      title="Paiement reçu"
      message="Merci ! Votre demande d'adhésion est enregistrée. Elle sera examinée par notre équipe dans les prochains jours ouvrés. Vous recevrez un email dès validation, avec votre numéro d'adhérent et votre carte numérique." />;
  }

  if (state.status === "approved") {
    return <ReturnLayout tone="success" icon={<CheckCircle2 className="h-12 w-12 text-emerald-500" />}
      title="Adhésion validée"
      message="Votre adhésion est active. Consultez votre espace membre pour voir votre carte et votre numéro d'adhérent."
      cta={{ href: "/espace-membre", label: "Espace membre" }} />;
  }

  if (state.status === "payment_failed") {
    return <ReturnLayout tone="error" icon={<XCircle className="h-12 w-12 text-red-500" />}
      title="Le paiement a échoué"
      message="Aucun montant n'a été prélevé. Vous pouvez recommencer votre demande."
      cta={{ href: "/adhesion", label: "Réessayer" }} />;
  }

  // pending_payment
  return <ReturnLayout tone="info" icon={<Clock className="h-12 w-12 text-accent" />}
    title="Paiement en cours de traitement"
    message="Cette page se met à jour automatiquement dès confirmation par notre prestataire de paiement." />;
}

function ReturnLayout({ tone, icon, title, message, cta }) {
  return (
    <div className="min-h-screen bg-secondary flex items-center justify-center px-4" data-testid={`adhesion-return-${tone}`}>
      <Card className="max-w-lg w-full border-border">
        <CardContent className="p-10 text-center space-y-4">
          <div className="flex justify-center">{icon}</div>
          <h1 className="font-heading text-2xl text-primary">{title}</h1>
          <p className="text-muted-foreground">{message}</p>
          {cta && (
            <Link to={cta.href} className="inline-block mt-4 px-6 h-11 leading-[44px] bg-accent hover:bg-accent/90 text-white rounded-md text-sm font-medium">
              {cta.label}
            </Link>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
